import Service from '@ember/service';
import setDefaultOptions from 'linced-app/utils/set-default-options';
import { saveAs } from 'file-saver';

const defaultConfig = {
  download: true,
  returnBlob: false,
  fileName: 'export.csv',
  raw: false,
  separator: ',',
  withSeparator: true,
  autoQuote: false,
};

const needsQuoteRE = /[",\r\n]/;

/**
 * Generates and downloads CSV files from in-memory data.
 *
 * Extracted from `@linc-technologies/ember-spreadsheet-export`.
 * For `.xlsx` output (including multi-sheet workbooks, HTML table parsing,
 * and merged cells), use the companion `excel` service instead.
 *
 * Data is an array of arrays, where each inner array is one row and the
 * first row is the heading row:
 *
 * ```javascript
 * import Component from '@glimmer/component';
 * import { service } from '@ember/service';
 * import { action } from '@ember/object';
 *
 * export default class ExportButton extends Component {
 *   @service csv;
 *
 *   @action export() {
 *     this.csv.export(
 *       [
 *         ['Title 1', 'Title 2', 'Title 3'],
 *         ['row1cell1', 'row1cell2', 'row1cell3'],
 *         ['row2cell1', 'row2cell2', 'row2cell3'],
 *       ],
 *       { fileName: 'export.csv', autoQuote: true }
 *     );
 *   }
 * }
 * ```
 */
export default class CsvService extends Service {
  /**
   * Converts `data` to CSV and (by default) triggers a browser download.
   *
   * @param {Array<Array>|string} data - Array of row arrays; the first row is
   *   treated as the heading row. A JSON string of the same shape is also
   *   accepted and parsed. Cell values may be strings, numbers, booleans,
   *   null/undefined (rendered empty), or moment objects (anything with a
   *   `_d` Date property).
   * @param {Object} [options]
   * @param {boolean} [options.download=true] - Trigger a file download via file-saver.
   * @param {boolean} [options.returnBlob=false] - Return the generated `Blob`
   *   instead of (or as well as) downloading. Useful for uploading or testing.
   * @param {string} [options.fileName='export.csv'] - Downloaded file name.
   * @param {string} [options.separator=','] - Field separator character.
   * @param {boolean} [options.withSeparator=true] - Prefix the file with a
   *   `sep=,` hint line so Excel opens it with the right separator. Set to
   *   false when the CSV is consumed by anything other than Excel, as the
   *   hint line is a non-standard Excel extension.
   * @param {boolean} [options.raw=false] - Emit values verbatim with no
   *   quoting at all. The output is not valid CSV if values contain the
   *   separator, quotes, or newlines.
   * @param {boolean} [options.autoQuote=false] - Only quote values that need
   *   it (those containing `"`, `,`, or newlines). By default, every value is
   *   quoted.
   * @returns {Blob|undefined} The CSV blob when `options.returnBlob` is true.
   */
  export(data, options) {
    options = setDefaultOptions(options, defaultConfig);

    let csv = this.jsonToCsv(data, options);
    let output = new Blob([csv], { type: 'data:text/csv;charset=utf-8' });

    if (options.download) {
      saveAs(output, options.fileName);
    }
    if (options.returnBlob) {
      return output;
    }
  }

  /**
   * Serialises an array of row arrays into a CRLF-delimited CSV string.
   * Used internally by `export`.
   *
   * Call directly if you need the CSV text without creating a Blob.
   *
   * @param {Array<Array>|string} objArray - Same shape as `export`'s `data`.
   * @param {Object} options - Fully-populated options (see `export`); no
   *   defaults are applied here.
   * @returns {string} The CSV content.
   */
  jsonToCsv(objArray, options) {
    let array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;

    let str = '';
    let line = '';
    let value;

    if (options.withSeparator) {
      // add separator identifier;
      str += `sep=${options.separator}\r\n`;
    }

    // add heading row
    let head = array[0];
    for (let i = 0; i < head.length; i++) {
      value = head[i] + '';
      if (i > 0) {
        line += options.separator;
      }
      line += this.quoteValue(value, options);
    }

    str += line + '\r\n';

    // add items
    for (let i = 1; i < array.length; i++) {
      line = '';

      for (let index = 0; index < array[i].length; index++) {
        value = array[i][index];

        if (index > 0) {
          line += options.separator;
        }
        if (typeof value === 'object') {
          if (value) {
            let resolveValue;
            if (value._d instanceof Date) {
              // dealing with encoding issue in IE browsers.
              resolveValue =
                value._d.getMonth() +
                1 +
                '/' +
                value._d.getDate() +
                '/' +
                value._d.getFullYear();
            } else {
              resolveValue = value._d.toString();
            }

            line += this.quoteValue(resolveValue, options);
          } else {
            line += this.quoteValue('', options);
          }
        } else {
          value = value + '';
          if (value && value !== 'undefined') {
            line += this.quoteValue(value, options);
          } else {
            line += this.quoteValue('', options);
          }
        }
      }

      str += line + '\r\n';
    }
    return str;
  }

  /**
   * Applies the configured quoting strategy to a single cell value:
   * verbatim when `options.raw`, quote-if-needed when `options.autoQuote`,
   * otherwise always quoted.
   *
   * @param {string} value - The stringified cell value.
   * @param {Object} options - Fully-populated options (see `export`).
   * @returns {string} The value, quoted per the options.
   */
  quoteValue(value, options) {
    switch (true) {
      case options.raw:
        return value;
      case options.autoQuote:
        if (value.match(needsQuoteRE)) {
          return this.doQuoteValue(value);
        }
        return value;
    }
    return this.doQuoteValue(value);
  }

  /**
   * Wraps a value in double quotes, escaping embedded quotes by doubling
   * them (`"` becomes `""`) per RFC 4180.
   *
   * @param {string} value - The stringified cell value.
   * @returns {string} The quoted value.
   */
  doQuoteValue(value) {
    return '"' + value.replace(/"/g, '""') + '"';
  }
}
